import { Injectable } from '@angular/core';
import { PosDataProvider } from '../tokens';
import { OfflineStateService } from './offline-state.service';
import { OfflineSequenceService } from './offline-sequence.service';
import { calculateTaxes, LocalTax, LocalTaxRule } from '../engines/offline-tax.engine';
import type {
  PosInitResult, CartData, CartResult, CheckoutData, CheckoutResult,
  OfflineProduct, OfflineStock, OfflinePricingRule, OfflineTaxRule,
} from '../types/offline-models';

@Injectable({ providedIn: 'root' })
export class OfflinePosService implements PosDataProvider {
  constructor(
    private offlineState: OfflineStateService,
    private sequenceService: OfflineSequenceService,
  ) {}

  async getInit(warehouseId: number): Promise<PosInitResult> {
    const db = this.offlineState.getDb();
    if (!db) throw new Error('Offline DB not initialized');

    const [products, stocks, pricingRules, taxRules] = await Promise.all([
      db.products.find({ selector: { isActive: true } }).exec(),
      db.stocks.find({ selector: { warehouseId } }).exec(),
      db.pricing_rules.find({ selector: { isActive: true } }).exec(),
      db.tax_rules.find({ selector: { isActive: true } }).exec(),
    ]);

    return {
      products: products.map(d => d.toJSON() as OfflineProduct),
      stocks: stocks.map(d => d.toJSON() as OfflineStock),
      pricingRules: pricingRules.map(d => d.toJSON() as OfflinePricingRule),
      taxRules: taxRules.map(d => d.toJSON() as OfflineTaxRule),
    };
  }

  async calculate(cart: CartData): Promise<CartResult> {
    let subtotal = 0;
    for (const line of cart.lines) {
      subtotal += line.quantity * line.unitPrice;
    }

    // Load tax rules + tax definitions from RxDB
    const db = this.offlineState.getDb();
    if (!db) {
      return { subtotal, taxAmount: 0, total: subtotal };
    }

    try {
      const [taxRuleDocs, taxRefDocs] = await Promise.all([
        db.tax_rules.find({ selector: { isActive: true } }).exec(),
        db.reference_data.find({ selector: { type: 'taxes' } }).exec(),
      ]);

      const taxRules: LocalTaxRule[] = taxRuleDocs.map((d: { toJSON: () => Record<string, unknown> }) => d.toJSON() as unknown as LocalTaxRule);
      const taxes: LocalTax[] = taxRefDocs.map((d: { toJSON: () => Record<string, unknown> }) => {
        const raw = d.toJSON() as Record<string, unknown>;
        return {
          id: raw['id'] as number,
          code: (raw['code'] as string) || '',
          name: (raw['label'] as string) || '',
          rate: (raw['rate'] as number) || 0,
          taxCategory: raw['taxCategory'] as string | undefined,
          calculationOrder: (raw['calculationOrder'] as number) || 0,
          taxOnTax: (raw['taxOnTax'] as boolean) || false,
          taxOnTaxRef: raw['taxOnTaxRef'] as string | undefined,
          isCompound: (raw['isCompound'] as boolean) || false,
          isActive: (raw['isActive'] as boolean) ?? true,
        };
      });

      if (taxes.length === 0) {
        return { subtotal, taxAmount: 0, total: subtotal };
      }

      const items = cart.lines.map(line => ({
        productId: line.productId,
        amount: line.quantity * line.unitPrice,
      }));

      const result = calculateTaxes(items, taxRules, taxes);
      return { subtotal: result.subtotal, taxAmount: result.taxAmount, total: result.total };
    } catch {
      // Fallback: return without tax if engine fails
      return { subtotal, taxAmount: 0, total: subtotal };
    }
  }

  async checkout(data: CheckoutData): Promise<CheckoutResult> {
    const db = this.offlineState.getDb();
    if (!db) throw new Error('Offline DB not initialized');

    const invoiceNumber = await this.sequenceService.generateInvoiceNumber(data.userId);
    const now = new Date().toISOString();
    const offlineId = crypto.randomUUID();

    let subTotal = 0;
    const orderLines = data.lines.map(line => {
      const totalPrice = line.quantity * line.unitPrice;
      subTotal += totalPrice;
      return { ...line, totalPrice };
    });

    // Calculate taxes using the offline tax engine
    const cartResult = await this.calculate({
      lines: data.lines.map(l => ({ productId: l.productId, quantity: l.quantity, unitPrice: l.unitPrice })),
    });

    const salesOrder = {
      id: String(Date.now()),
      orderNumber: invoiceNumber,
      customerId: data.customerId ?? null,
      orderDate: now,
      status: 'COMPLETED',
      subTotal,
      totalTax: cartResult.taxAmount,
      totalTaxWithheld: 0,
      totalAmount: cartResult.total,
      currency: null,
      notes: null,
      lines: orderLines,
      companyId: data.companyId,
      offlineId,
      invoiceNumber,
      syncStatus: 'PENDING',
      createdAt: now,
      updatedAt: now,
    };

    await db.sales_orders.insert(salesOrder);

    // La quantite de stock n'est PLUS mutee ici — ecart Y1.
    //
    // Le code precedent faisait `stock.incrementalPatch({ quantity: currentQty
    // - line.quantity })` : une lecture-modification-ecriture sur un agregat
    // replique, hors ligne. Deux postes vendant 3 unites sur un stock de 47
    // ecrivaient chacun 44 ; la verite est 41, et l'information perdue ne se
    // recupere pas.
    //
    // Les trois references de l'audit l'interdisent unanimement : Odoo cree un
    // document de mouvement que le serveur traite, ERPNext laisse le serveur
    // decider d'ecrire au grand livre, Medusa reserve cote serveur et n'expose
    // jamais la quantite au client.
    //
    // La commande inseree ci-dessus EST le document. La quantite affichee se
    // derive desormais de l'instantane moins les ventes en attente — voir
    // quantiteDisponible(). L'instantane reste ce que le serveur a envoye ; il
    // n'est jamais reecrit.

    await this.offlineState.refreshPendingCount();
    return { success: true, orderNumber: invoiceNumber, offlineId };
  }

  /**
   * Quantite disponible d'un produit, DERIVEE et non lue.
   *
   * Remplace la lecture directe de stocks.quantity, qui etait mutee localement
   * (ecart Y1). La formule est celle de Medusa : quantite en stock moins
   * quantite reservee.
   *
   *   disponible = instantane serveur − lignes des ventes en attente
   *
   * L'instantane n'est jamais reecrit : il reste ce que le serveur a envoye.
   * Deux postes qui vendent hors ligne derivent donc chacun une disponibilite
   * juste POUR EUX, et le serveur tranche a la remontee — au lieu que le
   * dernier a synchroniser ecrase le premier.
   */
  async quantiteDisponible(
    productId: string,
    warehouseId: number,
  ): Promise<number> {
    const db = this.offlineState.getDb();
    if (!db) return 0;

    const stock = await db.stocks
      .findOne({ selector: { productId, warehouseId } })
      .exec();
    const instantane = stock
      ? ((stock.toJSON() as OfflineStock).quantity ?? 0)
      : 0;

    // Ventes saisies localement et pas encore remontees : elles ont consomme
    // du stock que l'instantane ignore encore.
    const enAttente = await db.sales_orders
      .find({ selector: { syncStatus: 'PENDING' } })
      .exec();

    let reserve = 0;
    for (const commande of enAttente) {
      const lignes =
        (commande.toJSON() as { lines?: { productId: string; quantity: number }[] })
          .lines ?? [];
      for (const l of lignes) {
        if (l.productId === productId) reserve += Number(l.quantity ?? 0);
      }
    }

    return instantane - reserve;
  }
}
